import React from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon, Share2Icon, HomeIcon, GiftIcon, TicketIcon, StarIcon, UserCheckIcon, ExternalLinkIcon } from 'lucide-react';
interface ScoreSubmitProps {
  gameMode: string;
  players: string[];
  scores: Record<string, number[]>;
  onShare: () => void;
  onPlayAgain: () => void;
}
export function ScoreSubmit({
  gameMode,
  players,
  scores,
  onShare,
  onPlayAgain
}: ScoreSubmitProps) {
  return <div className="flex flex-col h-full bg-brand-charcoal relative overflow-hidden" data-id="element-308">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(184,150,90,0.1)_0%,transparent_60%)]" data-id="element-309" />

      <div className="relative z-10 flex-1 flex flex-col p-6 overflow-y-auto" data-id="element-310">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-8 pb-8" data-id="element-311">
          {/* Header */}
          <div className="text-center mt-6" data-id="element-312">
            <h2 className="text-3xl font-heading text-brand-cream-light mb-2" data-id="element-313">
              Great Round!
            </h2>
            <p className="text-brand-cream/60 font-body text-sm" data-id="element-314">
              You're entered to win amazing prizes
            </p>
          </div>

          {/* Representative Message */}
          <div className="bg-brand-green-deep/40 p-6 rounded-2xl border border-brand-cream/10 text-center space-y-4" data-id="element-315">
            <a href="https://form.typeform.com/to/uwnw3hfe" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center space-x-2 bg-brand-gold text-brand-charcoal font-heading text-lg py-4 rounded-xl shadow-lg hover:bg-brand-gold-light transition-colors active:scale-[0.98]" data-id="element-316">
              <ExternalLinkIcon className="w-5 h-5" data-id="element-317" />
              <span data-id="element-318">Submit Your Details</span>
            </a>

            <div className="flex items-center space-x-3" data-id="element-319">
              <div className="h-px bg-brand-cream/10 flex-1" data-id="element-320" />
              <span className="text-brand-cream/40 text-xs font-bold uppercase tracking-widest" data-id="element-321">
                or
              </span>
              <div className="h-px bg-brand-cream/10 flex-1" data-id="element-322" />
            </div>

            <div className="mx-auto w-14 h-14 rounded-full bg-brand-gold/20 flex items-center justify-center" data-id="element-323">
              <UserCheckIcon className="w-7 h-7 text-brand-gold" data-id="element-324" />
            </div>
            <p className="text-brand-cream-light font-heading text-lg leading-relaxed" data-id="element-325">
              Provide your name and details to a{' '}
              <span className="text-brand-gold" data-id="element-326">PuttLab Representative</span> to
              be entered to win a prize.
            </p>
            <p className="text-brand-cream/50 font-body text-xs" data-id="element-327">
              A representative is available at the PuttLab station.
            </p>
          </div>

          {/* Prizes Section */}
          <div data-id="element-328">
            <div className="flex items-center justify-center space-x-2 mb-6" data-id="element-329">
              <div className="h-px bg-brand-cream/10 flex-1" data-id="element-330" />
              <span className="text-brand-gold text-xs font-bold uppercase tracking-widest" data-id="element-331">
                Available Prizes
              </span>
              <div className="h-px bg-brand-cream/10 flex-1" data-id="element-332" />
            </div>

            <div className="space-y-4" data-id="element-333">
              {/* Grand Prize */}
              <div className="bg-gradient-to-br from-brand-gold/20 to-brand-charcoal border border-brand-gold/40 rounded-2xl p-5 relative overflow-hidden" data-id="element-334">
                <div className="absolute top-0 right-0 w-24 h-24 bg-brand-gold/10 rounded-bl-full" data-id="element-335" />
                <div className="flex items-start space-x-4 relative z-10" data-id="element-336">
                  <div className="p-3 rounded-xl bg-brand-gold/20 text-brand-gold" data-id="element-337">
                    <TrophyIcon className="w-6 h-6" data-id="element-338" />
                  </div>
                  <div data-id="element-339">
                    <span className="text-brand-gold text-[10px] font-bold uppercase tracking-widest block mb-1" data-id="element-340">
                      Grand Prize
                    </span>
                    <h4 className="font-heading text-xl text-brand-cream-light mb-1" data-id="element-341">
                      2-some at TPC Toronto
                    </h4>
                    <p className="text-brand-cream/60 text-xs font-body" data-id="element-342">
                      The ultimate golf experience.
                    </p>
                  </div>
                </div>
              </div>

              {/* Other Prizes Grid */}
              <div className="grid grid-cols-2 gap-3" data-id="element-343">
                <div className="bg-brand-cream/5 border border-brand-cream/10 rounded-xl p-4 flex flex-col items-center text-center" data-id="element-344">
                  <StarIcon className="w-5 h-5 text-brand-sand mb-2" data-id="element-345" />
                  <span className="font-heading text-sm text-brand-cream-light" data-id="element-346">
                    Signed Ferraro Hockey Stick
                  </span>
                </div>
                <div className="bg-brand-cream/5 border border-brand-cream/10 rounded-xl p-4 flex flex-col items-center text-center" data-id="element-347">
                  <TicketIcon className="w-5 h-5 text-brand-sand mb-2" data-id="element-348" />
                  <span className="font-heading text-sm text-brand-cream-light" data-id="element-349">
                    4 Blue Jays Tickets
                  </span>
                </div>
                <div className="bg-brand-cream/5 border border-brand-cream/10 rounded-xl p-4 flex flex-col items-center text-center" data-id="element-350">
                  <GiftIcon className="w-5 h-5 text-brand-sand mb-2" data-id="element-351" />
                  <span className="font-heading text-sm text-brand-cream-light" data-id="element-352">
                    $250 Bar Locale Gift Card
                  </span>
                </div>
                <div className="bg-brand-cream/5 border border-brand-cream/10 rounded-xl p-4 flex flex-col items-center text-center" data-id="element-353">
                  <GiftIcon className="w-5 h-5 text-brand-sand mb-2" data-id="element-354" />
                  <span className="font-heading text-sm text-brand-cream-light" data-id="element-355">
                    $150 Olive & Fig Gift Card
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="w-full space-y-3 mt-auto pt-2" data-id="element-356">
            <button onClick={onShare} className="w-full flex items-center justify-center space-x-2 bg-brand-gold text-brand-charcoal font-heading text-lg py-4 rounded-xl shadow-lg hover:bg-brand-gold-light transition-colors active:scale-[0.98]" data-id="element-357">
              <Share2Icon className="w-5 h-5" data-id="element-358" />
              <span data-id="element-359">Share Result</span>
            </button>

            <button onClick={onPlayAgain} className="w-full flex items-center justify-center space-x-2 bg-brand-cream-light/5 text-brand-cream-light font-body py-4 rounded-xl border border-brand-cream/10 hover:bg-brand-cream-light/10 transition-colors active:scale-[0.98]" data-id="element-360">
              <HomeIcon className="w-5 h-5 text-brand-gold" data-id="element-361" />
              <span data-id="element-362">Play Again</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}